/**
 * Keyboard navigation between cells of the test-case grid.
 *
 * Navigable cells are marked with a `data-grid-cell` attribute (see
 * `useGridCell`). Navigation walks the DOM table structure rather than any
 * React state, so it stays correct as columns/rows mount and unmount.
 */

/** Rows moved per PageUp/PageDown. */
const PAGE_ROWS = 10

const NAV_SELECTOR = "[data-grid-cell]"

function isNavCell(cell: Element | undefined | null): cell is HTMLElement {
  return cell instanceof HTMLElement && cell.hasAttribute("data-grid-cell")
}

function tbodyRows(row: HTMLTableRowElement): HTMLTableRowElement[] {
  const tbody = row.closest("tbody")
  return tbody ? Array.from(tbody.rows) : []
}

/** First navigable cell in a row, or null. */
function firstNavCell(row: HTMLTableRowElement): HTMLElement | null {
  return row.querySelector<HTMLElement>(NAV_SELECTOR)
}

/** Last navigable cell in a row, or null. */
function lastNavCell(row: HTMLTableRowElement): HTMLElement | null {
  const cells = Array.from(row.cells).filter(isNavCell)
  return cells[cells.length - 1] ?? null
}

/**
 * Nearest navigable cell in `targetRow` aligned to `colIndex`; falls back to the
 * first navigable cell in that row.
 */
function alignedNavCell(
  targetRow: HTMLTableRowElement,
  colIndex: number,
): HTMLElement | null {
  const cells = Array.from(targetRow.cells)
  if (colIndex < cells.length && isNavCell(cells[colIndex])) {
    return cells[colIndex]
  }
  return firstNavCell(targetRow)
}

function verticalTarget(
  td: HTMLTableCellElement,
  rowDelta: number,
): HTMLElement | null {
  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  const rows = tbodyRows(row)
  const rowIndex = rows.indexOf(row)
  if (rowIndex === -1) {
    return null
  }
  const colIndex = Array.from(row.cells).indexOf(td)
  const targetIndex = Math.min(
    Math.max(rowIndex + rowDelta, 0),
    rows.length - 1,
  )
  const targetRow = rows[targetIndex]
  if (!targetRow || targetRow === row) {
    return null
  }
  return alignedNavCell(targetRow, colIndex)
}

function horizontalTarget(
  td: HTMLTableCellElement,
  step: 1 | -1,
): HTMLElement | null {
  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  const cells = Array.from(row.cells)
  const colIndex = cells.indexOf(td)
  for (let i = colIndex + step; i >= 0 && i < cells.length; i += step) {
    if (isNavCell(cells[i])) {
      return cells[i]
    }
  }
  return null
}

/**
 * Tab-style advance that wraps across rows: move to the next/previous navigable
 * cell, falling through to the adjacent row's first/last cell at the row edges.
 */
export function advanceCell(
  td: HTMLTableCellElement,
  direction: "next" | "prev" | "down" | "up",
): HTMLElement | null {
  if (direction === "down") {
    return verticalTarget(td, 1)
  }
  if (direction === "up") {
    return verticalTarget(td, -1)
  }

  const step = direction === "next" ? 1 : -1
  const inRow = horizontalTarget(td, step)
  if (inRow) {
    return inRow
  }

  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  const rows = tbodyRows(row)
  const rowIndex = rows.indexOf(row)
  const wrapRow = rows[rowIndex + step]
  if (!wrapRow) {
    return null
  }
  return step === 1 ? firstNavCell(wrapRow) : lastNavCell(wrapRow)
}

/**
 * Handle an arrow / Home / End / PageUp / PageDown keypress on a focused cell.
 * Returns true when the event was a navigation key (and was handled).
 */
export function handleCellKeyNav(
  e: React.KeyboardEvent<HTMLTableCellElement>,
  td: HTMLTableCellElement,
): boolean {
  const { key } = e
  let target: HTMLElement | null = null

  switch (key) {
    case "ArrowLeft":
      target = horizontalTarget(td, -1)
      break
    case "ArrowRight":
      target = horizontalTarget(td, 1)
      break
    case "ArrowUp":
      target = verticalTarget(td, -1)
      break
    case "ArrowDown":
      target = verticalTarget(td, 1)
      break
    case "Home":
      target = e.ctrlKey || e.metaKey ? topOfColumn(td) : rowEdge(td, "start")
      break
    case "End":
      target = e.ctrlKey || e.metaKey ? bottomOfColumn(td) : rowEdge(td, "end")
      break
    case "PageUp":
      target = verticalTarget(td, -PAGE_ROWS)
      break
    case "PageDown":
      target = verticalTarget(td, PAGE_ROWS)
      break
    default:
      return false
  }

  e.preventDefault()
  if (target) {
    focusAndScroll(target)
  }
  return true
}

function rowEdge(
  td: HTMLTableCellElement,
  edge: "start" | "end",
): HTMLElement | null {
  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  return edge === "start" ? firstNavCell(row) : lastNavCell(row)
}

function topOfColumn(td: HTMLTableCellElement): HTMLElement | null {
  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  const rows = tbodyRows(row)
  const colIndex = Array.from(row.cells).indexOf(td)
  return rows[0] ? alignedNavCell(rows[0], colIndex) : null
}

function bottomOfColumn(td: HTMLTableCellElement): HTMLElement | null {
  const row = td.parentElement as HTMLTableRowElement | null
  if (!row) {
    return null
  }
  const rows = tbodyRows(row)
  const colIndex = Array.from(row.cells).indexOf(td)
  const last = rows[rows.length - 1]
  return last ? alignedNavCell(last, colIndex) : null
}

/** Focus a cell and keep it within the scroll container's viewport. */
export function focusAndScroll(target: HTMLElement): void {
  target.focus()

  const row = target.closest("tr")
  const scrollContainer = target.closest(".test-case-scroll-container")
  if (!row || !scrollContainer) {
    return
  }

  const rowRect = row.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()
  const headerHeight =
    scrollContainer.querySelector("thead")?.getBoundingClientRect().height ?? 0
  const footerHeight =
    scrollContainer.querySelector(".sticky.bottom-0")?.getBoundingClientRect()
      .height ?? 0
  const visibleTop = containerRect.top + headerHeight
  const visibleBottom = containerRect.bottom - footerHeight

  if (rowRect.top < visibleTop) {
    scrollContainer.scrollTop -= visibleTop - rowRect.top
  } else if (rowRect.bottom > visibleBottom) {
    scrollContainer.scrollTop += rowRect.bottom - visibleBottom
  }

  const cellRect = target.getBoundingClientRect()
  if (cellRect.left < containerRect.left) {
    scrollContainer.scrollLeft -= containerRect.left - cellRect.left
  } else if (cellRect.right > containerRect.right) {
    scrollContainer.scrollLeft += cellRect.right - containerRect.right
  }
}
